import React, { useState, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  useInAppBrowserDetection,
  type InAppBrowserType,
} from '@/hooks/precon-supporter';
import * as styles from './InAppBrowserWarningDialog.module.css';

/**
 * 注意事項の項目
 */
interface WarningItem {
  id: string;
  text: string;
}

/**
 * 注意事項リスト（将来の拡張用に配列で管理）
 */
const WARNING_ITEMS: WarningItem[] = [
  {
    id: 'indexeddb',
    text: '受講の進捗が記録されません。',
  },
  {
    id: 'download',
    text: '修了証発行が行えません。',
  },
];

/**
 * アプリ名の表示用ラベルを取得
 */
const getBrowserDisplayName = (browserType: InAppBrowserType): string => {
  switch (browserType) {
    case 'LINE':
      return 'LINE';
    case 'Instagram':
      return 'Instagram';
    case 'X':
      return 'X（旧Twitter）';
    case 'Facebook':
      return 'Facebook';
    case 'TikTok':
      return 'TikTok';
    case 'Threads':
      return 'Threads';
    case 'Messenger':
      return 'Messenger';
    case 'Google':
      return 'Google';
    case 'Bing':
      return 'Bing';
    case 'YouTube':
      return 'YouTube';
    default:
      return '';
  }
};

/**
 * アプリ内ブラウザ警告ダイアログコンポーネント
 *
 * LINE、Instagram、X(Twitter)、Facebookなどのアプリ内ブラウザからアクセスした場合に
 * 注意事項を表示し、SafariやChromeで開き直すことを促します。
 *
 * このダイアログは閉じることができません。ユーザーは標準ブラウザで開き直す必要があります。
 */
export const InAppBrowserWarningDialog: React.FC = () => {
  const { isInAppBrowser, browserType, isDetectionComplete } =
    useInAppBrowserDetection();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopied(true);
      // 3秒後にコピー状態をリセット
      setTimeout(() => setIsCopied(false), 3000);
    } catch {
      // フォールバック: 古いブラウザ向け
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      } catch {
        // コピーに失敗した場合は何もしない
      }
      document.body.removeChild(textArea);
    }
  }, []);

  // 検出完了前、またはアプリ内ブラウザでない場合は表示しない
  if (!isDetectionComplete || !isInAppBrowser) {
    return null;
  }

  const appName = getBrowserDisplayName(browserType);

  return (
    <Dialog.Root open={true}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlay} />
        <Dialog.Content
          className={styles.dialogContent}
          onEscapeKeyDown={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          onPointerDownOutside={e => e.preventDefault()}
        >
          <div className={styles.dialogPanel}>
            {/* ヘッダー */}
            <div className={styles.header}>
              <svg
                className={styles.warningIcon}
                width='28'
                height='28'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden='true'
              >
                <path
                  d='M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 19C1.55 19.36 1.64 19.7 1.82 20C2 20.3 2.26 20.56 2.56 20.74C2.86 20.92 3.2 21.01 3.56 21H20.44C20.8 21.01 21.14 20.92 21.44 20.74C21.74 20.56 22 20.3 22.18 20C22.36 19.7 22.45 19.36 22.45 19C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.97 3.15C12.67 2.98 12.34 2.89 12 2.89C11.66 2.89 11.33 2.98 11.03 3.15C10.73 3.32 10.47 3.56 10.29 3.86Z'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <Dialog.Title className={styles.dialogTitle}>
                標準ブラウザでのご利用を
                <br />
                お願いします
              </Dialog.Title>
            </div>

            {/* コンテンツ */}
            <div className={styles.contentArea}>
              <p className={styles.description}>
                {appName}アプリ内のブラウザからアクセスされています。
                <br />
                端末の標準ブラウザ（Safari、Google
                Chrome）でアクセスしてください。
              </p>

              {/* 注意事項リスト */}
              <ul className={styles.warningList}>
                {WARNING_ITEMS.map(item => (
                  <li key={item.id} className={styles.warningItem}>
                    <svg
                      className={styles.warningItemIcon}
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                    >
                      <circle
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='2'
                      />
                      <path
                        d='M12 8V12M12 16H12.01'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                      />
                    </svg>
                    <span className={styles.warningItemText}>{item.text}</span>
                  </li>
                ))}
              </ul>

              {/* 手順説明 */}
              <div className={styles.instructions}>
                <p className={styles.instructionsTitle}>
                  以下のいずれかの方法でアクセスしてください
                </p>
                <ul className={styles.instructionsList}>
                  <li>アプリのメニューから「ブラウザで開く」を選択</li>
                  <li>
                    下のボタンでURLをコピーし、標準ブラウザ（Google
                    Chrome、Microsoft Edge等）に貼り付け
                  </li>
                </ul>
              </div>
            </div>

            {/* アクションエリア */}
            <div className={styles.actionArea}>
              <button
                type='button'
                className={`${styles.copyButton} ${isCopied ? styles.copySuccess : ''}`}
                onClick={handleCopyUrl}
              >
                {isCopied ? (
                  <>
                    <svg
                      className={styles.copyButtonIcon}
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                    >
                      <path
                        d='M20 6L9 17L4 12'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    コピーしました
                  </>
                ) : (
                  <>
                    <svg
                      className={styles.copyButtonIcon}
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                    >
                      <rect
                        x='9'
                        y='9'
                        width='13'
                        height='13'
                        rx='2'
                        stroke='currentColor'
                        strokeWidth='2'
                      />
                      <path
                        d='M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5'
                        stroke='currentColor'
                        strokeWidth='2'
                      />
                    </svg>
                    このページのURLをコピー
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
